import React, { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { useUser } from '../context/UserContext';
import { useSettings } from '../context/SettingsContext';
import { useToast } from '../hooks/use-toast';
import { useRecaptcha } from '../hooks/useRecaptcha';
import usePageSEO from '../hooks/usePageSEO';
import { getLocalizedSiteName } from '../lib/siteName';

const ForgotPassword = () => {
  const { t, i18n } = useTranslation();
  const { forgotPassword } = useUser();
  const { settings } = useSettings();
  const { toast } = useToast();
  const { execute: executeRecaptcha, enabled: recaptchaEnabled } = useRecaptcha();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const lang = i18n.language?.substring(0, 2) || 'en';
  const siteName = getLocalizedSiteName(settings, lang);

  usePageSEO({ title: t('auth.forgotPassword'), noIndex: true });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const recaptchaToken = await executeRecaptcha('forgot_password');
      await forgotPassword(email, recaptchaToken);
      toast({ title: t('auth.resetLinkSent'), description: t('auth.resetLinkSentDesc', { name: siteName }) });
    } catch (error) {
      const msg = error.response?.data?.detail || t('auth.unableToSendResetLink');
      toast({ title: t('auth.error'), description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 flex items-center justify-center bg-gray-50" data-testid="forgot-password-page">
      <Card className="w-full max-w-md mx-4 shadow-xl">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">{t('auth.forgotPassword')}</CardTitle>
          <p className="text-gray-500 text-sm mt-1">{t('auth.forgotPasswordDesc', { name: siteName })}</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input data-testid="forgot-password-email" type="email" className="pl-10" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="john@example.com" />
              </div>
            </div>
            <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={loading} data-testid="forgot-password-submit">
              {loading ? t('auth.sendingResetLink') : t('auth.sendResetLink')}
            </Button>
            {recaptchaEnabled && (
              <p className="text-xs text-gray-400 text-center mt-2" data-testid="recaptcha-notice">
                Protected by Google <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">reCAPTCHA</a>
              </p>
            )}
          </form>
          <p className="text-center text-sm text-gray-500 mt-6">
            <Link to="/login" className="text-red-600 font-semibold hover:underline">{t('auth.backToSignIn')}</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
